import API_BASE from './api.js';

const WEB_SAFE_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/svg+xml',
]);

const MIME_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  svg: 'image/svg+xml',
};

function normalizeMime(type) {
  return String(type || '').trim().toLowerCase();
}

function inferMimeFromFilename(filename) {
  const name = String(filename || '').toLowerCase();
  const parts = name.split('.');
  if (parts.length < 2) return '';
  const ext = parts.pop();
  return MIME_BY_EXTENSION[ext] || '';
}

// Resizes a logo image to email-safe dimensions (max 500x150px) before uploading.
// Rasterizes SVGs to JPEG so they get the same cap (email clients render SVG
// unreliably and won't honor width/height on an untouched vector file).
// Keeps GIFs as-is to preserve animation; converts everything else to JPEG
// at 85% quality unless it's a PNG, which is kept as PNG to preserve transparency.
export async function resizeImageForLogo(file) {
  if (file.type === 'image/gif') return file;

  const MAX_W = 500;
  const MAX_H = 150;
  const isSvg = file.type === 'image/svg+xml';

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) {
        resolve(file);
        return;
      }
      if (!isSvg && w <= MAX_W && h <= MAX_H) {
        resolve(file);
        return;
      }

      const ratio = Math.min(MAX_W / w, MAX_H / h, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * ratio);
      canvas.height = Math.round(h * ratio);

      const outType = isSvg || file.type !== 'image/png' ? 'image/jpeg' : 'image/png';
      const outName = isSvg ? file.name.replace(/\.svg$/i, '.jpg') : file.name;

      try {
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            resolve(new File([blob], outName, { type: outType }));
          },
          outType,
          0.85
        );
      } catch {
        // Tainted canvas (e.g. an SVG referencing cross-origin resources) — fall back to the original file.
        resolve(file);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
}

export function validateWebImageFile(file) {
  if (!file) {
    return { ok: false, error: 'No image selected.' };
  }

  const mimeFromFile = normalizeMime(file.type);
  const mimeFromName = inferMimeFromFilename(file.name);
  const mime =
    WEB_SAFE_IMAGE_MIME_TYPES.has(mimeFromFile) ? mimeFromFile : mimeFromName;

  if (!WEB_SAFE_IMAGE_MIME_TYPES.has(mime)) {
    return {
      ok: false,
      error:
        'Unsupported image format. Please upload JPG, PNG, WEBP, GIF, AVIF, or SVG.',
    };
  }

  return { ok: true, mime };
}

// Uploads an image through a presigned S3 URL and returns the file's public URL.
export async function uploadImageFile(file) {
  const validation = validateWebImageFile(file);
  if (!validation.ok) throw new Error(validation.error);

  const token = localStorage.getItem('idToken');
  const signRes = await fetch(
    `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(
      file.name
    )}&type=${encodeURIComponent(validation.mime)}&size=${encodeURIComponent(file.size)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  if (!signRes.ok) throw new Error('Failed to get upload URL');

  const sign = await signRes.json();
  const uploadUrl = sign.uploadUrl || sign.url || sign.presignedUrl;
  const fileUrl = sign.fileUrl || sign.publicUrl;
  if (!uploadUrl || !fileUrl) throw new Error('Invalid upload response');

  const uploadRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': validation.mime },
    body: file,
  });
  if (!uploadRes.ok) throw new Error('Image upload failed');

  return fileUrl;
}
